import type { PositionDetail, Milestone } from './careerDetail'

// Source: HR/채용공고/[웹게시용] HTMLs, updated 2026-10-01.
// Demonstration capacities/temperatures corrected by the user on 2026-10-07.
// These October values do not overwrite the archived July/August copy.
const companyIntro = {
  en: [
    'GIGAette is a KAIST spin-off deep-tech startup founded in 2023. We store industrial waste heat and renewable energy, then deliver heat, steam, chilled water and electricity at the temperatures industrial processes need.',
    'Our core technology is IsoTES®, an isothermal thermal energy storage system. Instead of separate hot and cold tanks, a thermal boundary moves like a piston inside one tank to deliver heat at a constant temperature throughout discharge. Round-trip efficiency is over 95%.',
    'Our headquarters, corporate R&D institute and demonstration site are in Jukdong, Daejeon. We are building two plants for different temperature ranges: a 5 MWh plant at 200°C for low- and medium-temperature waste heat, and a 0.7 MWh plant at 500°C for high-temperature industries such as steel and aluminium. Core equipment completion is targeted for H2 2026, with full operation in early 2027.',
    'We supplied a 1.5 MWh district-heating plant in Tsetserleg, Mongolia, verifying benefits for 188 people under KOICA CTS Seed 1. A follow-on Seed 2 project expands capacity to 6 MWh with KRW 700 million in funding and is now in local installation and commissioning. The system stores heat using entirely solar electricity to provide village heating. We are moving from proving the technology to supplying it and scaling up.'
  ],
  ko: [
    '(주)기가에떼는 2023년 설립한 KAIST 스핀오프 딥테크 스타트업입니다. 산업 현장에서 그냥 버려지는 폐열과 재생에너지를 저장해 두었다가, 산업 공정에 필요한 온도의 열·증기·냉수·전기로 되돌려 줍니다.',
    '핵심 기술은 등온 열에너지 저장 시스템 IsoTES®입니다. 고온·저온 탱크를 따로 두는 기존 방식과 달리, 하나의 탱크 안에서 열 경계면이 피스톤처럼 움직이며 방전 전 구간에 걸쳐 일정한 온도의 열을 내보냅니다. 왕복 효율은 95% 이상입니다.',
    '대전 죽동에 본사·기업부설연구소와 실증 부지를 두고, 온도 대역이 다른 실증 설비 2기를 구축하고 있습니다 — 중저온 폐열을 다루는 5 MWh(200℃) 설비와 철강·알루미늄 같은 고온 산업을 겨냥한 0.7 MWh(500℃) 설비입니다. 2026년 하반기 핵심 설비 구축을 마치고 2027년 초 본격 가동을 목표로 하고 있습니다.',
    '해외에서는 이미 몽골 체체를렉에 1.5 MWh 지역난방 설비를 공급해 188명 수혜를 검증했고(KOICA CTS Seed 1), 현재 같은 지역에서 6 MWh 규모로 확장하는 후속 사업(Seed 2, 총 7억원)이 현지 설치·시운전 단계에 들어가 있습니다. 100% 태양광 전력만으로 열을 저장해 마을 난방을 공급하는 구성입니다. 기술을 증명하는 단계를 지나 실제로 공급하고 규모를 키우는 구간에 들어와 있습니다.'
  ]
}

const companyStats = [
  {
    label: {
      en: 'Total investment raised',
      ko: '누적 투자유치'
    },
    value: {
      en: 'KRW 2.1 billion',
      ko: '21억원'
    }
  },
  {
    label: {
      en: 'Government support secured',
      ko: '누적 확보 정부지원금'
    },
    value: {
      en: 'Approximately KRW 4.87 billion in R&D and commercialisation funding',
      ko: '약 48.7억원 · R&D·사업화 자금 합계'
    }
  }
]

const milestones: Milestone[] = [
  {
    date: '2023.05',
    text: {
      en: 'GIGAette incorporated as a KAIST faculty startup',
      ko: 'GIGAette 법인 설립 — KAIST 교원창업으로 출발'
    }
  },
  {
    date: '2024.04',
    text: {
      en: 'KOICA CTS Seed 1 started — Tsetserleg district-heating demonstration; KRW 300 million government support',
      ko: 'KOICA CTS Seed 1 사업 착수 — 몽골 체체를렉 지역난방 실증 · 정부지원 3억원'
    }
  },
  {
    date: '2025.04',
    text: {
      en: 'Tsetserleg demonstration plant opened — 1.5 MWh thermal storage; 188 direct beneficiaries',
      ko: '몽골 체체를렉 실증 설비 개소 — 1.5 MWh 열저장시스템 · 188명 직접 수혜'
    }
  },
  {
    date: '2025.08',
    text: {
      en: 'First seed investment — KRW 900 million; KRW 900 million cumulative',
      ko: 'Seed 1차 투자 유치 — 9억원 · 누적 9억원'
    }
  },
  {
    date: '2025.09',
    text: {
      en: 'Deep-Tech TIPS selected — IsoTES® development and demonstration R&D; KRW 1.5 billion government support',
      ko: '딥테크 팁스 선정 — IsoTES® 고도화 및 실증 R&D · 정부지원 15억원'
    }
  },
  {
    date: '2025.10',
    text: {
      en: 'KOICA CTS Seed 2 selected — notified on 27 October; KRW 700 million government support',
      ko: 'KOICA CTS Seed 2 선정 — 10월 27일 선정 통보 · 정부지원 7억원'
    }
  },
  {
    date: '2025.12',
    text: {
      en: 'KOICA CTS Seed 2 started on 10 December — expanding Tsetserleg district heating',
      ko: 'KOICA CTS Seed 2 사업 착수 — 12월 10일 시작 · 몽골 체체를렉 지역난방 사업 확장'
    }
  },
  {
    date: '2026.04',
    text: {
      en: 'Follow-on seed investment — KRW 700 million; KRW 1.6 billion cumulative',
      ko: '후속 Seed 투자 유치 — 7억원 · 누적 16억원'
    }
  },
  {
    date: '2026.04',
    text: {
      en: 'Eco Startup selected — Ministry of Environment carbon reduction and CCUS; KRW 80 million government support',
      ko: '에코스타트업 선정 — 환경부 탄소저감·CCUS 분야 · 정부지원 8,000만원'
    }
  },
  {
    date: '2026.05',
    text: {
      en: 'KODIT Start-up NEST selected — 19th cohort, deep-tech track; accelerator with no direct grant',
      ko: '신용보증기금 Start-up NEST 선정 — 제19기 딥테크특별전형 · 직접 지원금 없는 액셀러레이팅 프로그램'
    }
  },
  {
    date: '2026.08',
    text: {
      en: 'Startup City Project selected — Daejeon regional startup package; KRW 290 million government support',
      ko: '창업도시 조성 프로젝트 선정 — 대전 지역창업패키지 · 정부지원 2억 9,000만원'
    }
  },
  {
    date: '2026.08',
    text: {
      en: 'Follow-on seed investment — KRW 500 million; KRW 2.1 billion cumulative',
      ko: '후속 Seed 투자 유치 — 5억원 · 누적 21억원'
    }
  },
  {
    date: '2026.09',
    text: {
      en: 'Scale-up TIPS selected — MW-scale demonstration; approximately KRW 2 billion government support',
      ko: '스케일업 팁스 선정 — MW급 스케일업 실증 · 정부지원 약 20억원'
    }
  },
  {
    date: '2026.09',
    text: {
      en: 'Climate-tech top innovation award at Modu’s Challenge — startup league winner; KRW 40 million prize',
      ko: '모두의 챌린지 기후테크 최고혁신상 — 스타트업리그 우승 · 상금 4,000만원'
    }
  }
]

const projects = [
  {
    label: {
      en: 'Deep-Tech TIPS',
      ko: '딥테크 팁스'
    },
    value: {
      en: 'KRW 1.5 billion government support · IsoTES® development and demonstration R&D on our site',
      ko: '정부지원 15억원 · IsoTES® 고도화 및 자체 부지 실증 R&D'
    }
  },
  {
    label: {
      en: 'KOICA CTS Seed 2',
      ko: 'KOICA CTS Seed 2'
    },
    value: {
      en: 'KRW 700 million government support · Renewable-energy district heating in Tsetserleg, Mongolia',
      ko: '정부지원 7억원 · 몽골 체체를렉 재생에너지 연계 지역난방 사업'
    }
  },
  {
    label: {
      en: 'Scale-up TIPS',
      ko: '스케일업 팁스'
    },
    value: {
      en: 'Approximately KRW 2 billion government support · MW-scale demonstration of a constant-temperature-output thermal battery',
      ko: '정부지원 약 20억원 · 정온출력 열배터리 MW급 스케일업 실증'
    }
  },
  {
    label: {
      en: 'Startup City Project',
      ko: '창업도시 조성 프로젝트'
    },
    value: {
      en: 'KRW 290 million government support · Industrial waste-heat recovery/storage and carbon-free cooling demonstration',
      ko: '정부지원 2억 9,000만원 · 산업 폐열 회수·저장 및 무탄소 냉방 실증'
    }
  },
  {
    label: {
      en: 'Eco Startup',
      ko: '에코스타트업'
    },
    value: {
      en: 'KRW 80 million government support · CCUS application demonstration',
      ko: '정부지원 8,000만원 · CCUS 응용 실증'
    }
  },
  {
    label: {
      en: 'Start-up NEST',
      ko: 'Start-up NEST'
    },
    value: {
      en: 'Deep-tech accelerator with no direct grant',
      ko: '직접 지원금 없는 딥테크 액셀러레이팅 프로그램'
    }
  }
]

export const octoberPostingDetails: Record<string, PositionDetail> = {
  'thermal-systems-design-analysis-engineer': {
    saraminUrl: 'https://www.saramin.co.kr/zf_user/jobs/view?rec_idx=55186525',
    eyebrow: {
      en: 'Deep-Tech Thermal Energy Storage',
      ko: 'Deep-Tech Thermal Energy Storage'
    },
    subtitle: {
      en: 'Corporate R&D Institute · 5+ years · Target experience: 7–12 years',
      ko: '기업부설연구소 · 경력 5년 이상 · 적정 7–12년'
    },
    email: 'hr@gigaette.com',
    mission: {
      en: 'Design the thermal and mass balances and operating conditions of the complete IsoTES® system, connecting calculations to actual equipment specifications and operating criteria. Own thermal design decisions so the system achieves its target output and efficiency.',
      ko: 'IsoTES® 전체 시스템의 열·물질수지와 운전조건을 설계하고, 계산 결과를 실제 장비 사양과 운전 기준으로 연결합니다. 시스템이 목표 출력과 효율을 내도록 열설계 의사결정을 책임집니다.'
    },
    relatedPositions: true,
    responsibilities: {
      en: [
        'Establish steady-state and transient thermal and mass balances for the IsoTES® system',
        'Model glycerol- and molten-salt-based storage and discharge cycles integrated with steam, hot air and ORC systems',
        'Set equipment design points and operating ranges considering heat sources, storage capacity, target output, efficiency and pressure losses',
        'Prepare and maintain the Process Design Basis, PFD, equipment and line lists, and process content for P&IDs',
        'Compare demonstration data with models, analyse performance gaps and propose design and control improvements',
        'Define boundary conditions and performance requirements for heat exchanger, mechanical and electrical/control engineers, and manage design interfaces',
        'Prepare calculations, design criteria, performance forecasts, test plans and test reports'
      ],
      ko: [
        'IsoTES® 시스템의 정상상태·과도상태 열 및 물질수지 수립',
        '글리세롤·용융염 기반 열저장·방출 사이클과 증기·열풍·ORC 연계 시스템 모델링',
        '열원·저장용량·목표 출력·효율·압력손실을 반영한 장비별 설계점과 운전범위 결정',
        'Process Design Basis, PFD, 장비·라인 목록과 P&ID 공정 내용 작성·관리',
        '실증 데이터와 모델 비교, 성능 차이 원인 분석 및 설계·제어 개선안 제시',
        '열교환기·기계·전기제어 담당자에게 경계조건과 성능 요구사항을 정의하고 설계 인터페이스 관리',
        '계산서·설계기준서·성능예측서·시험계획서와 결과보고서 작성'
      ]
    },
    requirements: {
      en: [
        'A background in mechanical, chemical or energy engineering, or equivalent practical thermodynamics and heat-transfer expertise',
        '5+ years designing and analysing thermal or energy systems, chemical plants or power plants',
        'Experience drawing technical conclusions that connect thermal and mass balances with equipment specifications and operating conditions',
        'Ability to document assumptions, limitations and uncertainties and independently deliver work in your discipline'
      ],
      ko: [
        '기계·화공·에너지공학 관련 전공 또는 이에 준하는 열역학·열전달 실무 역량',
        '열시스템·에너지 시스템·화학 또는 발전 플랜트 설계·해석 5년 이상',
        '열·물질수지, 장비 사양과 운전조건을 연결해 기술적 결론을 내린 경험',
        '계산 가정·한계·불확실성을 문서화하고 자기 분야를 독립 완결할 수 있는 역량'
      ]
    },
    preferred: {
      en: [
        'Experience with thermal energy storage, CSP, heat pumps, ORC, steam or hot-air systems',
        'Experience with Python, MATLAB, EES, Aspen Plus, Aspen HYSYS or similar tools',
        'Experience with ANSYS Fluent, STAR-CCM+ or similar CFD tools',
        'Demonstration-plant commissioning, performance testing and model calibration',
        'Experience accounting for high-temperature fluid degradation, corrosion and freezing risks in design and operation'
      ],
      ko: [
        '열에너지 저장·CSP·열펌프·ORC·증기·열풍 시스템 경험',
        'Python·MATLAB·EES·Aspen Plus·Aspen HYSYS 또는 유사 도구 활용 경험',
        'ANSYS Fluent·STAR-CCM+ 또는 유사 CFD 도구 활용 경험',
        '실증설비 시운전·성능시험·모델 보정 경험',
        '고온 유체의 열화·부식·동결 위험을 설계와 운전에 반영한 경험'
      ]
    },
    whyThisRole: {
      title: {
        en: 'Bring individual equipment calculations into one system design basis',
        ko: '설비별 계산을 하나의 시스템 기준으로 묶어야 합니다'
      },
      body: {
        en: 'When heat sources, storage media, pumps, heat exchangers and users operate together, equipment-level calculations alone cannot guarantee overall performance. We need an engineer who can establish inputs across disciplines and own system-level criteria.',
        ko: '열원·저장매체·펌프·열교환기·수요처가 함께 움직이는 실증 단계에서는 장비별 계산만으로 전체 성능을 보장할 수 없습니다. 분야별 입력조건을 확정하고 시스템 관점의 기준을 세울 책임자가 필요합니다.'
      }
    },
    yourImpact: {
      title: {
        en: 'Your model becomes the equipment specification and operating standard',
        ko: '당신의 모델이 장비 사양과 운전 기준이 됩니다'
      },
      body: {
        en: 'Thermal duty and operating ranges from your model set the starting point for heat exchangers, piping, instrumentation and control. A baseline model informed by demonstration data improves both design speed and performance prediction for the next plant.',
        ko: '열모델에서 정한 duty와 운전범위는 열교환기·배관·계장·제어의 출발점이 됩니다. 실증 데이터를 반영한 기준 모델은 다음 호기의 설계 속도와 성능 예측 신뢰도를 함께 높입니다.'
      }
    },
    whatYouGet: {
      title: {
        en: 'Own the full cycle from modelling to site performance',
        ko: '모델에서 현장 성능까지 한 사이클을 책임집니다'
      },
      body: {
        en: 'Your calculations carry through equipment selection, commissioning, performance testing and model calibration. This is a senior individual contributor role: you establish the reasoning for your discipline without an in-house thermal analysis leader above you.',
        ko: '계산 결과를 보고서에 남기는 데서 끝나지 않고, 실제 장비 선정·시운전·성능시험·모델 보정까지 이어집니다. 사내 상위 열해석 리더 없이 자기 분야의 판단 근거를 세우는 senior IC 역할입니다.'
      }
    },
    conditions: [
      {
        label: {
          en: 'Location',
          ko: '근무지'
        },
        value: {
          en: '135 Jukdong-ro, Yuseong-gu, Daejeon (HQ + corporate R&D institute)',
          ko: '대전광역시 유성구 죽동로 135 (본사 + 기업부설연구소)'
        }
      },
      {
        label: {
          en: 'Employment',
          ko: '고용 형태'
        },
        value: {
          en: 'Full-time (3-month probation, same terms)',
          ko: '정규직 (수습 3개월, 동일 처우)'
        }
      },
      {
        label: {
          en: 'Working hours',
          ko: '근무 시간'
        },
        value: {
          en: '09:00–18:00',
          ko: '09:00–18:00'
        }
      },
      {
        label: {
          en: 'Travel',
          ko: '출장'
        },
        value: {
          en: 'Domestic and international travel to fabricators and demonstration sites may be required depending on the project',
          ko: '프로젝트에 따라 국내외 제작사·실증 현장 출장 가능'
        }
      },
      {
        label: {
          en: 'Annual salary',
          ko: '연봉'
        },
        value: {
          en: 'Negotiable after interview · From KRW 70 million per year',
          ko: '면접 후 협의 · 연봉 7,000만원 이상'
        }
      }
    ],
    applySubject: {
      en: '[Apply] SeniorThermalSystemsDesignAnalysisEngineer-',
      ko: '[지원] 열시스템설계해석책임엔지니어-'
    },
    apply: [
      {
        label: {
          en: 'Application period',
          ko: '모집 기간'
        },
        value: {
          en: 'Open until filled',
          ko: '채용 시까지'
        }
      },
      {
        label: {
          en: 'Closing',
          ko: '마감'
        },
        value: {
          en: 'Closes when a suitable candidate is hired',
          ko: '적합자 채용 시 마감'
        }
      },
      {
        label: {
          en: 'Email',
          ko: '이메일'
        },
        value: {
          en: 'hr@gigaette.com',
          ko: 'hr@gigaette.com'
        }
      },
      {
        label: {
          en: 'Subject format',
          ko: '제목 형식'
        },
        value: {
          en: '[Apply] SeniorThermalSystemsDesignAnalysisEngineer-YourName',
          ko: '[지원] 열시스템설계해석책임엔지니어-성함'
        }
      },
      {
        label: {
          en: 'Attachments',
          ko: '첨부 파일'
        },
        value: {
          en: 'Resume (PDF) + cover letter + portfolio or anonymised project description',
          ko: '이력서(PDF) + 자기소개서 + 포트폴리오 또는 익명화 프로젝트 기술서'
        }
      },
      {
        label: {
          en: 'Portfolio',
          ko: '포트폴리오'
        },
        value: {
          en: 'Show the equipment, your role, decisions you personally made and results (confidential information may be removed)',
          ko: '대상 설비·본인 역할·직접 내린 판단·결과가 드러나는 자료 (기밀 삭제 가능)'
        }
      },
      {
        label: {
          en: 'Recruiter',
          ko: '채용 담당'
        },
        value: {
          en: 'Ikje Jang (042-719-7544, hr@gigaette.com)',
          ko: '장익제 프로 (042-719-7544, hr@gigaette.com)'
        }
      }
    ],
    process: {
      steps: {
        en: [
          'Application',
          'Technical interview',
          'Executive interview',
          'Offer, terms & references',
          'Onboarding'
        ],
        ko: [
          '서류',
          '직무면접',
          '임원면접',
          '처우협의·레퍼런스',
          '입사'
        ]
      },
      note: {
        en: 'Total: about 3 weeks · Application review results are communicated individually after receipt',
        ko: '총 소요: 약 3주 · 서류 검토 결과는 접수 후 개별 안내드립니다'
      }
    },
    companyIntro,
    companyStats,
    milestones,
    projects
  },
  'heat-exchanger-thermal-design-engineer': {
    saraminUrl: 'https://www.saramin.co.kr/zf_user/jobs/view?rec_idx=55186578',
    eyebrow: {
      en: 'Deep-Tech Thermal Energy Storage',
      ko: 'Deep-Tech Thermal Energy Storage'
    },
    subtitle: {
      en: 'Corporate R&D Institute · 7+ years · Target experience: 8–15 years',
      ko: '기업부설연구소 · 경력 7년 이상 · 적정 8–15년'
    },
    email: 'hr@gigaette.com',
    mission: {
      en: 'Design heat exchangers that meet required heat duty and allowable pressure losses between high-temperature heat-transfer media and water, steam or air. Own the full flow from thermal design and performance guarantees to vendor coordination and performance verification.',
      ko: '고온 열매체와 물·증기·공기 사이에서 요구 열량과 허용 압력손실을 만족하는 열교환기를 설계합니다. 열설계와 성능보증부터 제작사 기술협의·성능검증까지 한 흐름으로 책임집니다.'
    },
    relatedPositions: true,
    responsibilities: {
      en: [
        'Compare and select heat exchanger types, including Shell & Tube, Coil and Air Cooler',
        'Rate and size heat exchangers and determine heat-transfer area, velocity, pressure losses and fouling margins',
        'Review thermal performance considering fluid properties, phase changes, viscosity changes and abnormal operating conditions',
        'Prepare heat exchanger datasheets, thermal design calculations and performance guarantee conditions',
        'Define the scope of CFD reviews and incorporate flow distribution, pressure-loss and heat-transfer results into design',
        'Check consistency of vendor calculations and drawings and verify FAT, performance-test and commissioning results'
      ],
      ko: [
        'Shell & Tube·Coil·Air Cooler 등 적용 후보 비교와 형식 선정',
        '열교환기 rating·sizing 및 전열면적·유속·압력손실·fouling margin 결정',
        '유체 물성·상변화·점도 변화와 비정상 운전조건을 반영한 열성능 검토',
        '열교환기 데이터시트·열설계 계산서·성능보증 조건 작성',
        'CFD 검토 범위 정의 및 유동분배·압력손실·열전달 결과의 설계 반영',
        '제작사 계산서·도면 정합성 검토와 FAT·성능시험·시운전 결과 검증'
      ]
    },
    requirements: {
      en: [
        'A background in mechanical, chemical or energy engineering, or equivalent practical expertise',
        '7+ years in industrial heat exchanger thermal design, rating and sizing',
        'Experience finalising specifications considering heat-transfer coefficients, pressure losses, fouling, materials and operating conditions together',
        'Experience leading vendor calculation, drawing and datasheet reviews and technical discussions',
        'Ability to independently document thermal performance decisions and design limitations'
      ],
      ko: [
        '기계·화공·에너지공학 관련 전공 또는 이에 준하는 실무 역량',
        '산업용 열교환기 열설계·rating·sizing 7년 이상',
        '열전달계수·압력손실·fouling·재질·운전조건을 함께 고려해 사양을 확정한 경험',
        '제작사 계산서·도면·데이터시트 검토와 기술협의를 주도한 경험',
        '열성능 판단과 설계 한계를 독립적으로 문서화할 수 있는 역량'
      ]
    },
    preferred: {
      en: [
        'Heat exchanger design with HTRI, Aspen EDR or similar tools',
        'Experience with ANSYS Fluent, STAR-CCM+ or similar CFD tools',
        'Projects based on TEMA and ASME Section VIII',
        'Experience with high-viscosity fluids, high-temperature heat-transfer media, molten salt, steam generators or air-cooled heat exchangers',
        'Projects spanning fabrication, inspection, performance testing and site commissioning'
      ],
      ko: [
        'HTRI·Aspen EDR 또는 유사 도구를 활용한 열교환기 설계 경험',
        'ANSYS Fluent·STAR-CCM+ 또는 유사 CFD 도구 활용 경험',
        'TEMA 및 ASME Section VIII 기반 프로젝트 경험',
        '고점도 유체·고온 열매체·용융염·증기발생기·공랭식 열교환기 경험',
        '제작·검사·성능시험·현장 시운전까지 이어지는 프로젝트 경험'
      ]
    },
    whyThisRole: {
      title: {
        en: 'Heat exchanger performance needs independent in-house judgement',
        ko: '열교환기 성능을 제작사 제안값에만 맡길 수 없습니다'
      },
      body: {
        en: 'IsoTES® output and efficiency depend directly on heat exchanger performance. We need an engineer who can independently check vendor proposals and set performance guarantee conditions considering operating conditions and pressure losses.',
        ko: 'IsoTES®의 출력과 효율은 열교환기 성능에 직접 연결됩니다. 제작사 제안값을 독립적으로 검산하고, 운전조건과 압력손실을 함께 보며 성능보증 조건을 정할 사내 책임자가 필요합니다.'
      }
    },
    yourImpact: {
      title: {
        en: 'Your thermal design basis shapes the performance of the next plant',
        ko: '한 번 세운 열설계 기준이 다음 호기의 성능을 좌우합니다'
      },
      body: {
        en: 'Reusable datasheets and review procedures speed up vendor comparison and technical discussions. Performance-test results feed into design margins and guarantee conditions for the next plant.',
        ko: '반복 사용 가능한 데이터시트와 검토 절차가 자리 잡으면 제작사 비교와 기술협의가 빨라지고, 성능시험 결과는 다음 설비의 설계 마진과 보증조건으로 이어집니다.'
      }
    },
    whatYouGet: {
      title: {
        en: 'Follow thermal design through to measured performance',
        ko: '열설계에서 실제 성능검증까지 이어갑니다'
      },
      body: {
        en: 'Detailed pressure-boundary mechanical design and statutory sign-off are handled by the plant mechanical engineer, fabricators and inspection bodies. You independently lead in-house decisions on thermal performance and performance guarantees.',
        ko: '압력경계의 상세 기계설계와 법정 서명은 플랜트 기계 담당·제작사·검사기관이 맡습니다. 이 역할은 열성능과 성능보증의 사내 판단을 독립적으로 주도합니다.'
      }
    },
    conditions: [
      {
        label: {
          en: 'Location',
          ko: '근무지'
        },
        value: {
          en: '135 Jukdong-ro, Yuseong-gu, Daejeon (HQ + corporate R&D institute)',
          ko: '대전광역시 유성구 죽동로 135 (본사 + 기업부설연구소)'
        }
      },
      {
        label: {
          en: 'Employment',
          ko: '고용 형태'
        },
        value: {
          en: 'Full-time (3-month probation, same terms)',
          ko: '정규직 (수습 3개월, 동일 처우)'
        }
      },
      {
        label: {
          en: 'Working hours',
          ko: '근무 시간'
        },
        value: {
          en: '09:00–18:00',
          ko: '09:00–18:00'
        }
      },
      {
        label: {
          en: 'Travel',
          ko: '출장'
        },
        value: {
          en: 'Domestic and international travel to fabricators and demonstration sites may be required depending on the project',
          ko: '프로젝트에 따라 국내외 제작사·실증 현장 출장 가능'
        }
      },
      {
        label: {
          en: 'Annual salary',
          ko: '연봉'
        },
        value: {
          en: 'Negotiable after interview · From KRW 80 million per year',
          ko: '면접 후 협의 · 연봉 8,000만원 이상'
        }
      }
    ],
    applySubject: {
      en: '[Apply] SeniorHeatExchangerThermalDesignEngineer-',
      ko: '[지원] 열교환기열설계책임엔지니어-'
    },
    apply: [
      {
        label: {
          en: 'Application period',
          ko: '모집 기간'
        },
        value: {
          en: 'Open until filled',
          ko: '채용 시까지'
        }
      },
      {
        label: {
          en: 'Closing',
          ko: '마감'
        },
        value: {
          en: 'Closes when a suitable candidate is hired',
          ko: '적합자 채용 시 마감'
        }
      },
      {
        label: {
          en: 'Email',
          ko: '이메일'
        },
        value: {
          en: 'hr@gigaette.com',
          ko: 'hr@gigaette.com'
        }
      },
      {
        label: {
          en: 'Subject format',
          ko: '제목 형식'
        },
        value: {
          en: '[Apply] SeniorHeatExchangerThermalDesignEngineer-YourName',
          ko: '[지원] 열교환기열설계책임엔지니어-성함'
        }
      },
      {
        label: {
          en: 'Attachments',
          ko: '첨부 파일'
        },
        value: {
          en: 'Resume (PDF) + cover letter + portfolio or anonymised project description',
          ko: '이력서(PDF) + 자기소개서 + 포트폴리오 또는 익명화 프로젝트 기술서'
        }
      },
      {
        label: {
          en: 'Portfolio',
          ko: '포트폴리오'
        },
        value: {
          en: 'Show the equipment, your role, decisions you personally made and results (confidential information may be removed)',
          ko: '대상 설비·본인 역할·직접 내린 판단·결과가 드러나는 자료 (기밀 삭제 가능)'
        }
      },
      {
        label: {
          en: 'Recruiter',
          ko: '채용 담당'
        },
        value: {
          en: 'Ikje Jang (042-719-7544, hr@gigaette.com)',
          ko: '장익제 프로 (042-719-7544, hr@gigaette.com)'
        }
      }
    ],
    process: {
      steps: {
        en: [
          'Application',
          'Technical interview',
          'Executive interview',
          'Offer, terms & references',
          'Onboarding'
        ],
        ko: [
          '서류',
          '직무면접',
          '임원면접',
          '처우협의·레퍼런스',
          '입사'
        ]
      },
      note: {
        en: 'Total: about 3 weeks · Application review results are communicated individually after receipt',
        ko: '총 소요: 약 3주 · 서류 검토 결과는 접수 후 개별 안내드립니다'
      }
    },
    companyIntro,
    companyStats,
    milestones,
    projects
  },
  'plant-mechanical-design-engineer': {
    saraminUrl: 'https://www.saramin.co.kr/zf_user/jobs/view?rec_idx=55186637',
    eyebrow: {
      en: 'Deep-Tech Thermal Energy Storage',
      ko: 'Deep-Tech Thermal Energy Storage'
    },
    subtitle: {
      en: 'Corporate R&D Institute · 5+ years · Target experience: 7–12 years',
      ko: '기업부설연구소 · 경력 5년 이상 · 적정 7–12년'
    },
    email: 'hr@gigaette.com',
    mission: {
      en: 'Turn process and thermal design requirements into mechanical systems that can be fabricated, installed and operated. Own P&IDs, layout, piping and equipment design through vendor coordination and site changes.',
      ko: '공정·열설계 요구사항을 제작·설치·운전 가능한 기계 시스템으로 구현합니다. P&ID·배치·배관·장치 설계부터 제작사 협의와 현장 변경까지 책임집니다.'
    },
    relatedPositions: true,
    responsibilities: {
      en: [
        'Integrate process content into P&IDs and design plant layouts, piping routes, tank, pressure-vessel and support concepts and interfaces, and maintenance clearances',
        'Review thermal expansion, supports, insulation, drains, vents and access for high-temperature piping',
        'Prepare equipment, piping, valve and instrumentation datasheets, BOMs, and purchasing and fabrication specifications',
        'Produce 3D models and fabrication/assembly drawings or review outsourced drawings and manage change history',
        'Resolve fabricator and contractor technical queries, site clashes, fabrication deviations and construction changes',
        'Participate in inspection, installation and commissioning and consolidate as-built design criteria'
      ],
      ko: [
        '공정 내용을 P&ID 도면에 통합하고 설비 배치·배관 routing·탱크·압력용기·지지구조의 개념·인터페이스와 정비 공간 설계',
        '고온 배관의 열팽창·지지·단열·배수·벤트와 접근성 검토',
        '장비·배관·밸브·계장품 데이터시트, BOM, 구매·제작 사양서 작성',
        '3D 모델·제작·조립 도면 작성 또는 외주 도면 검토와 변경 이력 관리',
        '제작사·시공사 기술질의, 현장 간섭·제작오차·시공 변경 해결',
        '검사·설치·시운전 참여와 as-built 설계 기준 정리'
      ]
    },
    requirements: {
      en: [
        'A background in mechanical, plant, naval architecture or chemical-plant mechanical engineering, or equivalent practical expertise',
        '5+ years designing mechanical systems, piping or equipment for chemical, energy, power or industrial plants',
        'Experience reading P&IDs and designing plant layout, piping, equipment and support interfaces',
        'Experience coordinating technically with fabricators and contractors using 3D CAD and 2D fabrication drawings',
        'Ability to independently maintain consistency across drawings, BOMs and specifications through site changes'
      ],
      ko: [
        '기계·플랜트·조선·화공기계 관련 전공 또는 이에 준하는 실무 역량',
        '화학·에너지·발전·산업 플랜트 기계·배관·장치 설계 5년 이상',
        'P&ID를 읽고 설비 배치·배관·장치·지지구조 인터페이스를 설계한 경험',
        '3D CAD와 2D 제작도면을 활용한 제작사·시공사 기술협의 경험',
        '현장 변경까지 도면·BOM·사양서 정합성을 독립적으로 관리할 수 있는 역량'
      ]
    },
    preferred: {
      en: [
        'Experience with SolidWorks, Inventor, Creo, CATIA, AutoCAD Plant 3D or similar tools',
        'Application of ASME Section VIII, ASME B31 series or KGS Code',
        'Review of TEMA heat exchanger, pressure-vessel and high-temperature piping fabrication drawings',
        'Piping flexibility, thermal stress, structural, vibration or insulation design reviews',
        'On-site experience with outsourced fabrication, welding, inspection, installation and commissioning'
      ],
      ko: [
        'SolidWorks·Inventor·Creo·CATIA·AutoCAD Plant 3D 또는 유사 도구 사용 경험',
        'ASME Section VIII·ASME B31 계열·KGS Code 적용 경험',
        'TEMA 열교환기와 압력용기·고온 배관 제작도면 검토 경험',
        '배관 유연성·열응력·구조·진동·단열 설계 검토 경험',
        '외주 제작·용접·검사·설치·시운전 현장 경험'
      ]
    },
    whyThisRole: {
      title: {
        en: 'We need someone to turn system calculations into real hardware',
        ko: '계산된 시스템을 실제 제작물로 완성할 사람이 필요합니다'
      },
      body: {
        en: 'Even sound thermal calculations will not make a plant work unless tanks, piping, valves, pumps and supports are connected in a form that can be fabricated, installed and maintained. We need an engineer who records site changes as reusable design knowledge.',
        ko: '열계산이 맞더라도 탱크·배관·밸브·펌프·지지구조가 제작·설치·정비 가능한 형태로 연결되지 않으면 설비는 작동하지 않습니다. 현장 변경까지 설계 자산으로 축적할 책임자가 필요합니다.'
      }
    },
    yourImpact: {
      title: {
        en: 'Your drawings become fabrication standards for the next plant',
        ko: '당신의 도면이 다음 설비의 제작 기준이 됩니다'
      },
      body: {
        en: 'Consistency across P&IDs, layouts, piping, BOMs and fabrication specifications reduces omissions and site changes. As-built drawings and change history become standards for the next project.',
        ko: 'P&ID·배치·배관·BOM·제작사양의 정합성이 높아지면 제작 누락과 현장 변경이 줄어듭니다. as-built와 변경 이력이 다음 프로젝트의 표준으로 이어집니다.'
      }
    },
    whatYouGet: {
      title: {
        en: 'Work from basic design through fabrication and installation',
        ko: '기본설계에서 제작·설치까지 관통합니다'
      },
      body: {
        en: 'You lead in-house mechanical basic design and outsourced drawing reviews. Detailed pressure-boundary and structural calculations and statutory sign-off are handled by fabricators, licensed firms and inspection bodies; you responsibly review their interfaces and deliverables.',
        ko: '사내 기계 기본설계와 외주 도면 검토를 주도합니다. 압력경계·구조의 상세 계산과 법정 서명은 제작사·면허 보유 업체·검사기관이 맡으며, 이 역할은 그 인터페이스와 결과를 책임 있게 검토합니다.'
      }
    },
    conditions: [
      {
        label: {
          en: 'Location',
          ko: '근무지'
        },
        value: {
          en: '135 Jukdong-ro, Yuseong-gu, Daejeon (HQ + corporate R&D institute)',
          ko: '대전광역시 유성구 죽동로 135 (본사 + 기업부설연구소)'
        }
      },
      {
        label: {
          en: 'Employment',
          ko: '고용 형태'
        },
        value: {
          en: 'Full-time (3-month probation, same terms)',
          ko: '정규직 (수습 3개월, 동일 처우)'
        }
      },
      {
        label: {
          en: 'Working hours',
          ko: '근무 시간'
        },
        value: {
          en: '09:00–18:00',
          ko: '09:00–18:00'
        }
      },
      {
        label: {
          en: 'Travel',
          ko: '출장'
        },
        value: {
          en: 'Domestic and international travel to fabricators and demonstration sites may be required depending on the project',
          ko: '프로젝트에 따라 국내외 제작사·실증 현장 출장 가능'
        }
      },
      {
        label: {
          en: 'Annual salary',
          ko: '연봉'
        },
        value: {
          en: 'Negotiable after interview · From KRW 70 million per year',
          ko: '면접 후 협의 · 연봉 7,000만원 이상'
        }
      }
    ],
    applySubject: {
      en: '[Apply] SeniorPlantMechanicalDesignEngineer-',
      ko: '[지원] 플랜트기계설계책임엔지니어-'
    },
    apply: [
      {
        label: {
          en: 'Application period',
          ko: '모집 기간'
        },
        value: {
          en: 'Open until filled',
          ko: '채용 시까지'
        }
      },
      {
        label: {
          en: 'Closing',
          ko: '마감'
        },
        value: {
          en: 'Closes when a suitable candidate is hired',
          ko: '적합자 채용 시 마감'
        }
      },
      {
        label: {
          en: 'Email',
          ko: '이메일'
        },
        value: {
          en: 'hr@gigaette.com',
          ko: 'hr@gigaette.com'
        }
      },
      {
        label: {
          en: 'Subject format',
          ko: '제목 형식'
        },
        value: {
          en: '[Apply] SeniorPlantMechanicalDesignEngineer-YourName',
          ko: '[지원] 플랜트기계설계책임엔지니어-성함'
        }
      },
      {
        label: {
          en: 'Attachments',
          ko: '첨부 파일'
        },
        value: {
          en: 'Resume (PDF) + cover letter + portfolio or anonymised project description',
          ko: '이력서(PDF) + 자기소개서 + 포트폴리오 또는 익명화 프로젝트 기술서'
        }
      },
      {
        label: {
          en: 'Portfolio',
          ko: '포트폴리오'
        },
        value: {
          en: 'Show the equipment, your role, decisions you personally made and results (confidential information may be removed)',
          ko: '대상 설비·본인 역할·직접 내린 판단·결과가 드러나는 자료 (기밀 삭제 가능)'
        }
      },
      {
        label: {
          en: 'Recruiter',
          ko: '채용 담당'
        },
        value: {
          en: 'Ikje Jang (042-719-7544, hr@gigaette.com)',
          ko: '장익제 프로 (042-719-7544, hr@gigaette.com)'
        }
      }
    ],
    process: {
      steps: {
        en: [
          'Application',
          'Technical interview',
          'Executive interview',
          'Offer, terms & references',
          'Onboarding'
        ],
        ko: [
          '서류',
          '직무면접',
          '임원면접',
          '처우협의·레퍼런스',
          '입사'
        ]
      },
      note: {
        en: 'Total: about 3 weeks · Application review results are communicated individually after receipt',
        ko: '총 소요: 약 3주 · 서류 검토 결과는 접수 후 개별 안내드립니다'
      }
    },
    companyIntro,
    companyStats,
    milestones,
    projects
  },
  'electrical-control-engineer-2026': {
    saraminUrl: 'https://www.saramin.co.kr/zf_user/jobs/view?rec_idx=55186712',
    eyebrow: {
      en: 'Deep-Tech Thermal Energy Storage',
      ko: 'Deep-Tech Thermal Energy Storage'
    },
    subtitle: {
      en: 'Corporate R&D Institute · 3+ years · Hands-on electrical experience required',
      ko: '기업부설연구소 · 경력 3년 이상 · 전기 실무 필수'
    },
    email: 'hr@gigaette.com',
    mission: {
      en: 'Implement panel ratings, electrical drawings, PLC and HMI based on the control concept and core logic set by the CTO. Your panels and logic complete the electrical control of IsoTES® demonstration plants.',
      ko: 'CTO가 정한 제어 개념과 핵심 로직을 바탕으로 판넬 정격 산정부터 전기 도면·PLC·HMI 구현까지 직접 수행합니다. 당신이 구성하는 판넬과 로직이 IsoTES® 실증 설비의 전기 제어를 완성합니다.'
    },
    relatedPositions: true,
    responsibilities: {
      en: [
        'Size panel components: MCCB ratings, cable sizes and protection coordination',
        'Prepare switchboard and control-panel drawings with EPLAN, AutoCAD Electrical or similar tools',
        'Implement PLC programs based on the control concept and core logic provided by the CTO',
        'Build HMI screens and implement alarms and interlocks',
        'Connect sensors, transmitters, valves and VFDs and support I/O checkout and loop checks',
        'Review panel-builder drawings, handle technical coordination and support site commissioning'
      ],
      ko: [
        '판넬 부품 정격 산정 — MCCB 용량·케이블 굵기·차단 협조',
        '배전반·제어반 도면 작성 (EPLAN·AutoCAD Electrical 또는 유사 도구)',
        'CTO가 제공하는 제어 개념·핵심 로직을 바탕으로 PLC 프로그램 구현',
        'HMI 화면 구성과 알람·인터록 로직 적용',
        '센서·트랜스미터·밸브·VFD 연결, I/O checkout·loop check 지원',
        '판넬 제작업체 도면 검토·기술 소통 및 현장 시운전 지원'
      ]
    },
    requirements: {
      en: [
        'A background in electrical, electronic, control/instrumentation or mechatronics engineering, or equivalent practical expertise',
        '3+ years of practical electrical and control experience on industrial equipment',
        'Experience designing or building low-voltage panels and switchboards, including breaker and cable sizing',
        'Experience personally producing electrical and control drawings',
        'Experience running and commissioning PLC programs on real equipment'
      ],
      ko: [
        '전기·전자·제어계측·메카트로닉스 관련 전공 또는 이에 준하는 실무 역량',
        '산업설비 전기·제어 분야 3년 이상 실무 경험',
        '저압 판넬·배전반 설계 또는 제작 경험 (차단기·케이블 정격 산정)',
        '전기·제어 도면을 직접 작성한 경험',
        'PLC 프로그램을 실제 설비에서 구동·시운전한 경험'
      ]
    },
    preferred: {
      en: [
        'Siemens, LS ELECTRIC, Rockwell or similar PLC, HMI or SCADA platforms',
        'EPLAN, AutoCAD Electrical or similar electrical drawing tools',
        'Industrial communications including Modbus, PROFINET or EtherNet/IP',
        'FAT, SAT, loop checks, I/O tests and on-site troubleshooting',
        'Electrical design near explosion-proof or high-voltage equipment, or commissioning energy/plant equipment',
        'Relevant Korean certifications such as Engineer Electricity, Industrial Engineer Electricity or Engineer Electric Work'
      ],
      ko: [
        'Siemens·LS ELECTRIC·Rockwell 또는 유사 PLC·HMI·SCADA 플랫폼 경험',
        'EPLAN·AutoCAD Electrical 또는 유사 전기도면 도구 경험',
        'Modbus·PROFINET·EtherNet/IP 등 산업통신 구성 경험',
        'FAT·SAT·loop check·I/O test와 현장 트러블슈팅 경험',
        '방폭·고압 설비 인근 전기 설계 또는 에너지·플랜트 설비 시운전 경험',
        '전기기사·전기산업기사·전기공사기사 등 관련 자격'
      ]
    },
    whyThisRole: {
      title: {
        en: 'The number of plants needing electrical and control implementation is growing',
        ko: '전기·제어로 구현할 설비가 빠르게 늘고 있습니다'
      },
      body: {
        en: 'As demonstration plants increase, so does the work of sizing components, drawing panels and implementing PLC and HMI. The CTO helps define the control concept and core logic; we need someone to make them work on real equipment.',
        ko: '실증 설비가 늘면서 정격을 산정하고 도면을 그리고 PLC·HMI를 구현할 일이 함께 늘고 있습니다. 제어 개념과 핵심 로직은 CTO가 함께 잡고, 이를 실제 설비에서 동작하게 만들 실무자를 찾습니다.'
      }
    },
    yourImpact: {
      title: {
        en: 'Your implementation becomes the starting point for the next plant',
        ko: '구현한 기준이 다음 설비의 출발점이 됩니다'
      },
      body: {
        en: 'Documented panel configurations, drawings, programs and I/O test results save the next plant from repeating the same work. Problems and improvements verified on site help us build the next plant faster.',
        ko: '판넬 구성·도면·프로그램·I/O 시험 결과가 정리되면 다음 호기에서 같은 작업을 처음부터 반복하지 않아도 됩니다. 현장에서 확인한 문제와 개선점이 다음 설비의 구축 속도로 이어집니다.'
      }
    },
    whatYouGet: {
      title: {
        en: 'Follow drawings through to operating equipment',
        ko: '도면에서 실제 설비 구동까지 경험합니다'
      },
      body: {
        en: 'Start with panel ratings and electrical drawings and continue through PLC/HMI implementation, I/O checks and commissioning. You complete real equipment in collaboration with the CTO and thermal/mechanical engineers, rather than owning all control specifications alone.',
        ko: '판넬 정격과 전기 도면에서 시작해 PLC·HMI 구현, I/O 확인과 시운전까지 이어집니다. 모든 제어 사양을 혼자 책임지는 역할이 아니라 CTO 및 열·기계 담당자와 협업해 실제 설비를 완성하는 자리입니다.'
      }
    },
    conditions: [
      {
        label: {
          en: 'Location',
          ko: '근무지'
        },
        value: {
          en: '135 Jukdong-ro, Yuseong-gu, Daejeon (HQ + corporate R&D institute)',
          ko: '대전광역시 유성구 죽동로 135 (본사 + 기업부설연구소)'
        }
      },
      {
        label: {
          en: 'Employment',
          ko: '고용 형태'
        },
        value: {
          en: 'Full-time (3-month probation, same terms)',
          ko: '정규직 (수습 3개월, 동일 처우)'
        }
      },
      {
        label: {
          en: 'Working hours',
          ko: '근무 시간'
        },
        value: {
          en: '09:00–18:00',
          ko: '09:00–18:00'
        }
      },
      {
        label: {
          en: 'Travel',
          ko: '출장'
        },
        value: {
          en: 'Domestic and international travel to fabricators and demonstration sites may be required depending on the project',
          ko: '프로젝트에 따라 국내외 제작사·실증 현장 출장 가능'
        }
      },
      {
        label: {
          en: 'Annual salary',
          ko: '연봉'
        },
        value: {
          en: 'Negotiable after interview · From KRW 60 million per year',
          ko: '면접 후 협의 · 연봉 6,000만원 이상'
        }
      }
    ],
    applySubject: {
      en: '[Apply] ElectricalControlEngineer-',
      ko: '[지원] 전기제어엔지니어-'
    },
    apply: [
      {
        label: {
          en: 'Application period',
          ko: '모집 기간'
        },
        value: {
          en: 'Open until filled',
          ko: '채용 시까지'
        }
      },
      {
        label: {
          en: 'Closing',
          ko: '마감'
        },
        value: {
          en: 'Closes when a suitable candidate is hired',
          ko: '적합자 채용 시 마감'
        }
      },
      {
        label: {
          en: 'Email',
          ko: '이메일'
        },
        value: {
          en: 'hr@gigaette.com',
          ko: 'hr@gigaette.com'
        }
      },
      {
        label: {
          en: 'Subject format',
          ko: '제목 형식'
        },
        value: {
          en: '[Apply] ElectricalControlEngineer-YourName',
          ko: '[지원] 전기제어엔지니어-성함'
        }
      },
      {
        label: {
          en: 'Attachments',
          ko: '첨부 파일'
        },
        value: {
          en: 'Resume (PDF) + cover letter + portfolio or anonymised project description',
          ko: '이력서(PDF) + 자기소개서 + 포트폴리오 또는 익명화 프로젝트 기술서'
        }
      },
      {
        label: {
          en: 'Portfolio',
          ko: '포트폴리오'
        },
        value: {
          en: 'Show the equipment, your role, decisions you personally made and results (confidential information may be removed)',
          ko: '대상 설비·본인 역할·직접 내린 판단·결과가 드러나는 자료 (기밀 삭제 가능)'
        }
      },
      {
        label: {
          en: 'Recruiter',
          ko: '채용 담당'
        },
        value: {
          en: 'Ikje Jang (042-719-7544, hr@gigaette.com)',
          ko: '장익제 프로 (042-719-7544, hr@gigaette.com)'
        }
      }
    ],
    process: {
      steps: {
        en: [
          'Application',
          'Technical interview',
          'Executive interview',
          'Offer, terms & references',
          'Onboarding'
        ],
        ko: [
          '서류',
          '직무면접',
          '임원면접',
          '처우협의·레퍼런스',
          '입사'
        ]
      },
      note: {
        en: 'Total: about 3 weeks · Application review results are communicated individually after receipt',
        ko: '총 소요: 약 3주 · 서류 검토 결과는 접수 후 개별 안내드립니다'
      }
    },
    companyIntro,
    companyStats,
    milestones,
    projects
  },
}
